// Always-animated raid map tiles. Each grid cell gets a live TILE_PX canvas
// and a cached still layer: a frame is a blit of that layer, a multiply for
// the hour, then only the things that actually move (canopies, lamps, clouds
// — see the static/dynamic split in roomImageGen.js). World-space effects
// sample by grid row/col, so neighboring tiles stay seamless without a
// whole-map canvas.
import { createTile, refreshStatic, composite, selectRecipe, ROOM_RECIPES, TILE_PX } from './roomImageGen.js';
import { getRaidSitePhase, dayLight } from './worldClock-core.js';
import { partyAt, stepClock, tileStage, normalisePlan } from './raidTeamAnim-core.js';
import { drainRaidFx, liveFx, clearRaidFx } from './raidFx-core.js';
import { drawTeam } from './raidTeamAnim.js';
import * as config from '../constants.js';

const BASE_WIND = 0.35;   // gust formula peaks at 0.42 — never clamps
const CLOUD_BASE = 0.3;   // three decks stack, so less cover reads as more sky

// Ambient motion here is slow: clouds are drawn in 2px cells and drift
// 3-7 world px/s (a visible step every ~0.4s), tree sway rounds to a ±1px
// twitch over a ~3.5s cycle, and the lamp strobe is aliased at any frame
// rate. 12fps is indistinguishable from 30 and costs a third as much.
//
// The map still slows when another window has focus, because rAF keeps firing
// at full rate on an unfocused-but-VISIBLE window and there is no reason to
// spend a core on an animation nobody is attending to. But it slows far less
// than it used to. This was 500ms — 2fps — and that was a workaround for the
// canopies redrawing themselves 55 discs at a time, which made a visible map
// cost enough to stutter video playing in another window. They are blitted
// now, the map costs a third of what it did, and the video does not care.
//
// 2fps was also below the content's own floor: the fastest thing on screen is
// the cloud drift at ~2.5 visible steps a second, so sampling it twice a second
// UNDERSAMPLES it and the sky jumps instead of drifting. 6fps clears that with
// margin, and it matters because this is an idle game — a raid left running on
// a second monitor while you work is the ordinary way to watch one, not an edge
// case, and it is the exact case this governs. The window being properly hidden
// is already handled: the browser stops rAF entirely and neither figure applies.
const FRAME_MS = 83;        // ~12fps, focused
const BLUR_FRAME_MS = 166;  // ~6fps, another window has focus
const LAYOUT_MS = 250;      // cadence for the layout-forcing DOM reads

let animCells = [];
// WHERE on the planet the grid on screen is. One raid is one place, so one
// clock serves every cell. The region id comes along only to name the site in
// the readout and to stand in for a pre-S9 save that has no site.
let animRegionId = null;
let animSite = null;
let rafId = null;
let lastFrame = 0;
let cloudX = 0, cloudY = 0;
let drawCursor = 0;         // round-robin position for the frame budget
let lastClockUpdate = 0;
let maxCol = 0;             // grid width, for viewport column culling
let lastLayoutCheck = 0;
let gridVisible = false;
let vpEl = null;
let vpW = 0;                // viewport client width
let colW = 0;               // grid column width; 0 = no horizontal culling

// The team walking the selected path, or null when no raid is running. Pushed
// in by the raid loop once a second (setRaidParty); the position between those
// updates is interpolated here off the raid's own schedule, so the figures
// move at frame rate while the game keeps ticking at 1Hz.
let party = null;

// Outcome effects still playing — a check passing, a synergy firing, somebody
// taking a hit, an item coming up. Posted by the raid's resolution code, which
// is headless and knows nothing about this module, and collected here each
// frame. See raidFx-core.js.
let fx = [];

// How far from the party a tile has to be before it is left to the ordinary
// round-robin. Figures are drawn within DRAW_R (a party straddling a seam
// lands on both tiles); cells out to FORCE_R are redrawn every frame anyway,
// so the tile the team has just walked off is cleared of them immediately
// rather than whenever the rotation next reaches it.
const DRAW_R = 1.2;
const FORCE_R = 2.2;

// Per-frame composite budget. Compositing is a blit, a multiply, the short
// dynamic list and the cloud pass — ~0.14ms a cell, against ~0.44ms for the
// full redraw it replaces. At 120 the whole of a tier-3 grid (153 cells)
// still lands inside the frame cost the 40px renderer used to pay for 48 of
// them. Cells rotate round-robin, so exceeding the budget just means a cell
// refreshes every other frame — adjacent tiles a frame apart diverge by
// <1px of cloud drift, below visibility.
const FRAME_BUDGET = 120;

// Still-layer repaints per frame. The cached layer only changes when the
// sun moves the shadows a whole unit (a couple of dozen times a game day),
// and then EVERY cell needs it — so the sweep is spread across frames
// instead of hitching. 4/frame clears a tier-3 grid in ~3s of the ~24s
// between changes.
const STATIC_BUDGET = 4;

const reducedMotion = typeof matchMedia === 'function'
    && matchMedia('(prefers-reduced-motion: reduce)').matches;

// LOCAL day phase at the raid's SITE (0 = midnight, 0.5 = noon) — the single
// source of truth shared by the tile palette and the day-clock bar above the
// grid. Was a global wall-clock phase until S9; the world clock keyed it to a
// place, so two raids at different longitudes now show different suns — even
// two raids in the same region.
export function getDayPhase() {
    return getRaidSitePhase(animRegionId, animSite).phase;
}

/**
 * Tell the animation loop where this grid IS. Called once per grid, before the
 * cells are registered — a per-cell argument would be the same value 153 times
 * and would let a half-registered grid disagree with itself about its own sky.
 */
export function setRaidSite(regionId, site) {
    animRegionId = regionId;
    animSite = site || null;
}

// Is this room's committed encounter set indoors? The grid pins its events at
// generation (preRollGridEvents), so the tile can be drawn for the encounter it
// will actually run — an indoor one gets its roof cut away. Read out of the
// generated constants rather than importing the event system, which would drag
// the whole raid-loot graph into the renderer's call path for one string.
const envCache = new Map();
function isIndoorEncounter(room) {
    const id = room?.plannedEventIds?.[0];
    if (!id) return false;
    if (!envCache.has(id)) {
        let env = null;
        for (const list of [config.eventDefinitionsDominion, config.eventDefinitionsVeil, config.eventDefinitionsWilders]) {
            const found = list?.find(e => e.id === id);
            if (found) { env = found.environment; break; }
        }
        envCache.set(id, env);
    }
    return envCache.get(id) === 'indoor';
}

/**
 * Tell the map where the raid team is, or pass null to take them off it.
 *
 * Called from the raid progression loop (1Hz) with a snapshot of the raid's
 * schedule — `path` (selectedPath), `index` (currentRoomIndex), `raid` (for
 * roomStartTime/nextRoomTime, which a room-time consumable can slide) and
 * `members` (teamSprites of the live roster). Everything between two calls is
 * interpolated from that schedule, which is why a snapshot is enough.
 */
export function setRaidParty(p) {
    const was = party && party.pos;
    party = p && p.path && p.path.length && p.members && p.members.length
        ? { ...p, pos: null, stage: null, stageKey: null }
        : null;
    // A raid ending takes its outcomes with it: a flare still playing over a
    // completed grid is announcing something the player has already been paid
    // for. Reduced motion never starts a loop and so never draws these at all,
    // which is the right answer for a screen full of flashes anyway — the log
    // and the notifications carry the same news.
    if (!party) { fx = []; clearRaidFx(); setTransit(false); }
    if (party) party.pos = partyNow();
    // Nothing else repaints the tiles the team has LEFT. The animation loop
    // covers that while it is running (it redraws the neighbourhood every
    // frame), but two cases have no loop to rely on: reduced motion, which
    // never starts one, and the end of a raid, which clears the party and
    // would otherwise leave three figures standing on the last tile until the
    // round-robin happened past them.
    const cellOf = q => q && `${Math.round(q.row)},${Math.round(q.col)}`;
    if (reducedMotion || !party) {
        if (cellOf(was) !== cellOf(party && party.pos)) {
            redrawNearParty(was);
            redrawNearParty(party && party.pos);
        }
    }
}

// Resolve the party snapshot against the wall clock. Returns what partyAt
// returns, or null when no raid is running.
function partyNow() {
    if (!party) return null;
    const { elapsedMs, durationMs } = stepClock(party.raid, Date.now());
    // The step index comes off the LIVE raid when there is one, not off the
    // snapshot: the index and the schedule have to be from the same tick. A
    // room that ended between two pushes would otherwise pair a fresh
    // roomStartTime with the previous step and put the team back at the tile
    // they just left for the rest of the second.
    const index = party.raid?.currentRoomIndex ?? party.index;
    // The tile's own stage first: how far the team spreads decides how long the
    // spreading takes, and where it gathers decides how far it has to walk to
    // get there. Both come out of the tile's ground plan.
    const stage = stageFor(party.path?.[index], party.members.length);
    return partyAt(party.path, index, elapsedMs, durationMs, stage);
}

// What this tile makes of the party: the standing posts, how far they are
// spread, and where the party gathers. Worked out once per path step and
// cached, because it is a scan over the tile's footprints and it cannot change
// while the team is standing on it.
//
// The ground plan is the renderer's own record of where it put the buildings
// and the water, so the team is kept off exactly what the map drew rather than
// off a guess about what it might have drawn.
function stageFor(step, count) {
    if (!step) return null;
    const key = `${step.row},${step.col},${count}`;
    if (party.stageKey === key) return party.stage;
    const cell = animCells.find(c => c.row === step.row && c.col === step.col);
    const plan = normalisePlan(cell?.tile?.plan, TILE_PX);
    // Seeded by the tile, so a given room lays its posts out the same way every
    // time the team walks into it, exactly as its art is the same every time.
    party.stageKey = key;
    party.stage = tileStage(plan, count, (step.row * 31 + step.col * 17) >>> 0, step.room?.type);
    return party.stage;
}

export function resetAnimCells() {
    // drop the cached still layers with the cells — a tier-3 grid holds ~9MB
    // of them, and a new raid rebuilds every tile anyway
    for (const c of animCells) c.tile.canvas = null;
    animCells = [];
    animRegionId = null;
    animSite = null;
    party = null;
    fx = [];
    clearRaidFx();
    setTransit(false);
    drawCursor = 0;
    maxCol = 0;
    lastLayoutCheck = 0;
    gridVisible = false;
    vpEl = null;
    vpW = 0;
    colW = 0;
}

// offsetParent, scrollWidth and clientWidth each force a style + layout
// flush, so they're sampled on the slow cadence rather than every frame;
// only scrollLeft (which has to track the user's scrolling) is read live.
function refreshLayout() {
    const grid = document.getElementById('raid-grid');
    // Raid tab not visible — the loop stays scheduled but skips the work
    // (the browser already pauses rAF entirely when the window is hidden)
    gridVisible = !!grid && grid.offsetParent !== null;
    vpEl = document.getElementById('raid-grid-viewport');
    vpW = vpEl ? vpEl.clientWidth : 0;
    colW = (gridVisible && vpEl && maxCol > 0 && vpEl.scrollWidth > vpW)
        ? vpEl.scrollWidth / (maxCol + 1)
        : 0;
}

// ============================================================================
// DAY-CLOCK BAR (#raid-day-clock, above the raid grid)
// ============================================================================
// A window onto 1/3 of the game day: the gradient (one full day = 3 bar
// widths) and the dawn/noon/dusk/midnight icons scroll right-to-left as time
// passes, wrapping around; the fixed center marker is "now".
//
// Since S9 the bar reads the RAIDING REGION's local time, and says so: the
// readout is the only place the world clock is legible as a number, and
// "17:42" over an unnamed bar invites the player to assume it is the same
// 17:42 everywhere.

// Icon markers live in index.html as .raid-day-clock-icon spans with
// data-phase attributes — noon (0.5) and midnight (0). Sunrise and sunset are
// worldClock-core's SUNRISE/SUNSET, and they read off the gradient without
// needing their own glyphs.

// One static gradient sampling the daylight curve across a full day,
// night-navy -> dawn-amber -> noon-cream -> dusk-amber -> navy
function buildDayGradient() {
    const night = [26, 33, 58], day = [255, 236, 179], warm = [255, 171, 94];
    const stops = [];
    for (let i = 0; i <= 24; i++) {
        const phase = i / 24;
        const light = (dayLight(phase) - 0.4) / 0.6; // 0..1
        // blend night->warm->day: warm band in the transition
        const t = light, w = Math.max(0, 1 - Math.abs(t - 0.45) / 0.35); // warmth bump around transitions
        const mix = (a, b, f) => Math.round(a + (b - a) * f);
        const base = night.map((n, ch) => mix(n, day[ch], t));
        const rgb = base.map((b, ch) => mix(b, warm[ch], w * 0.55));
        stops.push(`rgb(${rgb.join(',')}) ${(phase * 100).toFixed(1)}%`);
    }
    return `linear-gradient(90deg, ${stops.join(', ')})`;
}

let clockInitialized = false;

function updateDayClock() {
    const track = document.getElementById('raid-day-clock-track');
    if (!track) return;
    if (!clockInitialized) {
        track.style.backgroundImage = buildDayGradient();
        track.style.backgroundSize = '300% 100%';
        track.style.backgroundRepeat = 'repeat-x';
        clockInitialized = true;
    }
    const local = getRaidSitePhase(animRegionId, animSite);
    const readout = document.getElementById('raid-day-clock-readout');
    if (readout) {
        const regionName = config.raidRegions?.[animRegionId]?.name;
        readout.textContent = `${local.icon} ${local.time}`;
        readout.title = regionName
            ? `${regionName} — local time at THIS raid site, ${local.name}. A region spans several hours of longitude, so the next raid there can meet a different sun. The map's daylight follows this clock.`
            : `Local time — ${local.name}`;
    }

    const width = track.clientWidth;
    if (!width) return;
    const dayPhase = local.phase;

    const lineNow = document.getElementById('raid-day-clock-now');
    const lineNowColor = (1 - dayLight(dayPhase)) * 255;
    lineNow.style.backgroundColor = `rgb(${lineNowColor},${lineNowColor},${lineNowColor})`;

    // gradient's t=dayPhase point sits at the bar center; repeat-x wraps
    track.style.backgroundPositionX = `${width / 2 - dayPhase * 3 * width}px`;
    for (const el of track.querySelectorAll('.raid-day-clock-icon')) {
        const phase = parseFloat(el.dataset.phase);
        const delta = ((phase - dayPhase + 1.5) % 1) - 0.5; // days from now, [-0.5, 0.5)
        el.style.left = `${width / 2 + delta * 3 * width}px`;
    }
}

// Attach a live tile canvas to a grid cell and register it for the shared
// animation loop. Must be called after cell.innerHTML is set — the canvas
// is prepended so overlay content stays above it in paint order.
export function registerAnimCell(cellEl, room, conn, regionKey) {
    if (typeof document === 'undefined' || !room || !room.name) return;
    // Belt and braces for the tile palette, which is keyed to the region: a
    // caller that forgot setRaidSite still gets the right art, and the clock
    // falls back to the region capital rather than the meridian.
    if (!animRegionId) animRegionId = regionKey;
    const recipe = (typeof room.recipe === 'string' && ROOM_RECIPES[room.recipe])
        ? room.recipe
        : selectRecipe(room.name, room.category, room.type);
    // Position-salted seed: persisted row/col keep compositions stable
    // across reloads while twin rooms get distinct layouts
    const salt = room.row != null ? `@${room.row},${room.col}` : '';
    const canvas = document.createElement('canvas');
    canvas.width = TILE_PX;
    canvas.height = TILE_PX;
    canvas.className = 'room-tile-canvas';
    cellEl.prepend(canvas);
    const cell = {
        ctx: canvas.getContext('2d'),
        tile: createTile(regionKey, recipe, room.name, {
            salt, conn, roomType: room.type, col: room.col, row: room.row,
            // the skill this room's encounter tests, for its map anchor;
            // absent on roads, water, campsites, entry/exit and blocked cells
            primary: room.primary,
            indoor: isIndoorEncounter(room),
            // the generated road layer: which way the roadway leaves this tile,
            // and whether it is the trunk or a branch. Absent on most tiles.
            road: room.road,
            roadRank: room.roadRank,
            // steps to the nearest roadway: how built-up this tile is allowed
            // to be, so development thickens along the routes and thins out
            // behind them
            roadDist: room.roadDist,
            // the district this cell sits in: how built up the ground around it
            // is, and so how much growth and how worn a road gets. Distance to
            // a road cannot say that — there are busy back lots and empty
            // frontages — and it is what makes an outskirt look like one.
            zone: room.zone,
            // how far this cell is from the open ocean, in whole tiles, and
            // which edge of the map that is. Only a region the sea got into
            // has them; everywhere else they are absent and nothing floods.
            shore: room.shore,
            shoreSide: room.shoreSide,
            // the sublevel's rail spur — one column of the map, laid like a road
            rail: room.rail,
            // which sides a rest stop or shoreline carries its open ground
            // across, so two clearings side by side read as one
            clearing: room.clearing,
        }),
        row: room.row, col: room.col,
    };
    if (room.col != null && room.col > maxCol) maxCol = room.col;
    animCells.push(cell);
    // First frame immediately so the grid is never blank; for reduced-motion
    // users this is also the final (static) picture (clock included)
    refreshStatic(cell.tile, getDayPhase());
    drawCell(cell, animParams(performance.now(), 0), partyNow());
    updateDayClock();
    if (!reducedMotion && rafId == null) {
        lastFrame = 0;
        rafId = requestAnimationFrame(animFrame);
    }
}

function animParams(ts, dt) {
    const t = ts / 1000;
    const wind = BASE_WIND * (0.7 + 0.3 * Math.sin(t * 0.35) + 0.2 * Math.sin(t * 1.3));
    const dayPhase = getDayPhase();
    cloudX += dt * (3 + wind * 12);
    cloudY += dt * (1 + wind * 4);
    const cover = CLOUD_BASE * (0.85 + 0.3 * Math.sin(t * 0.04) + 0.15 * Math.sin(t * 0.017));
    // Effects are posted by the raid loop, which runs on the wall clock, so
    // they are timed against that rather than against `t` (which counts from
    // whenever the page happened to load).
    return { wind, dayPhase, t, nowMs: Date.now(), cloud: { cover, x: cloudX, y: cloudY } };
}

function drawCell(c, p, pos) {
    composite(c.ctx, c.tile, { ...p, row: c.row, col: c.col });
    // The team goes on last, over everything the tile is and everything
    // overhead. Their pixel position is relative to THIS canvas, so a party
    // between two tiles is drawn on both and each clips its own half.
    if (party && pos && Math.abs(pos.col - c.col) <= DRAW_R && Math.abs(pos.row - c.row) <= DRAW_R) {
        drawTeam(c.ctx,
            (pos.col - c.col + 0.5) * TILE_PX,
            (pos.row - c.row + 0.5) * TILE_PX,
            {
                party: pos, members: party.members, tSec: p.t, dayPhase: p.dayPhase, tile: TILE_PX,
                posts: party.stage?.postOffsets, resting: party.stage?.resting,
                fx, nowMs: p.nowMs,
            });
    }
}

// Repaint every tile within reach of `at`, off the animation loop's cadence.
// The team is drawn from the party's CURRENT position, so passing an old one
// here is how a tile they have left gets cleared.
function redrawNearParty(at) {
    if (!at || !animCells.length) return;
    const pos = partyNow();
    const p = animParams(typeof performance !== 'undefined' ? performance.now() : 0, 0);
    for (const c of animCells) {
        if (!nearParty(c, at)) continue;
        refreshStatic(c.tile, p.dayPhase);
        drawCell(c, p, pos);
    }
}

// ---- the current room's zoom, and why the map has to switch it off ----
// The room the team is in is magnified (styles.css `.current-room`), which is
// a good way to say "here" and a bad way to cross a boundary: the team is
// drawn on BOTH tiles either side of a seam and each canvas clips its own
// half, so a magnified tile beside an unmagnified one splits the party into
// two different sizes down the join.
//
// So the zoom is dropped for as long as the team is between tiles and put back
// once they have settled. `walking` is exactly that window — it is true for the
// walk out and the walk in and false for the whole dwell — and it starts a full
// half-tile before they reach the edge, which is seconds of room for the CSS
// transition to finish in. A road, where the team never stops, never zooms.
let transit = false;
function setTransit(on) {
    if (on === transit) return;
    transit = on;
    document.getElementById('raid-grid')?.classList.toggle('team-in-transit', on);
}

// Is this cell close enough to the party to need repainting every frame?
// Generous by a full tile beyond where a figure can be drawn, so leaving a
// tile clears it in the same frame.
function nearParty(c, pos) {
    return !!pos && c.col != null
        && Math.abs(pos.col - c.col) <= FORCE_R && Math.abs(pos.row - c.row) <= FORCE_R;
}

function animFrame(ts) {
    if (animCells.length === 0) {
        rafId = null;
        return;
    }
    rafId = requestAnimationFrame(animFrame);
    if (ts - lastFrame < (document.hasFocus() ? FRAME_MS : BLUR_FRAME_MS)) return;
    // dt caps at the background interval so cloud drift stays wall-clock
    // honest at 2fps, while a return from a long pause can't jump the sky
    const dt = lastFrame ? Math.min(BLUR_FRAME_MS / 1000, (ts - lastFrame) / 1000) : 0;
    lastFrame = ts;
    if (ts - lastLayoutCheck > LAYOUT_MS) {
        lastLayoutCheck = ts;
        refreshLayout();
    }
    if (!gridVisible) return;
    const p = animParams(ts, dt);
    // Collect whatever the raid resolved since the last frame, and drop what
    // has finished playing. Drained even with no party on the map so a queue
    // posted while the screen was elsewhere cannot arrive stale later.
    const posted = drainRaidFx();
    if (posted.length) fx = party ? fx.concat(posted) : [];
    if (fx.length) fx = liveFx(fx, p.nowMs);
    // Sweep the still layers back up to date after the sun moves them. The
    // check is cheap and returns false for a cell that is already current,
    // so scanning from the front each frame walks the sweep forward on its
    // own — no dirty queue to keep in sync with the cell list.
    for (let i = 0, repainted = 0; i < animCells.length && repainted < STATIC_BUDGET; i++) {
        if (refreshStatic(animCells[i].tile, p.dayPhase)) repainted++;
    }
    // Viewport column culling: #raid-grid-viewport scrolls horizontally on
    // tier 2/3, and the grid is uniform - the visible column window falls
    // straight out of the scroll offsets, no per-cell rect queries. The ±2
    // column margin absorbs the grid's inner padding and keeps edge tiles
    // fresh mid-scroll; a cell scrolled into view redraws within a frame
    // or two when the rotation reaches it.
    let colMin = -Infinity, colMax = Infinity;
    if (colW) {
        colMin = Math.floor(vpEl.scrollLeft / colW) - 2;
        colMax = Math.ceil((vpEl.scrollLeft + vpW) / colW) + 1;
    }
    // The team's own neighbourhood is drawn first and every frame, outside
    // both the rotation and the column cull: it is the one part of the map
    // that MUST be current, and it is a couple of dozen cells at most.
    const pos = partyNow();
    // Drop the current room's zoom while the team is crossing between tiles.
    setTransit(!!pos && pos.walking);
    const len = animCells.length;
    let drawn = 0;
    if (pos) {
        for (let i = 0; i < len; i++) {
            const c = animCells[i];
            if (!nearParty(c, pos)) continue;
            drawCell(c, p, pos);
            c.partyFrame = ts;
            drawn++;
        }
    }
    // Rotate through the rest under the frame budget (see FRAME_BUDGET);
    // culled cells cost nothing and don't consume budget, so the visible
    // window refreshes at full rate
    for (let scanned = 0; scanned < len && drawn < FRAME_BUDGET; scanned++) {
        const c = animCells[drawCursor % len];
        drawCursor++;
        if (c.partyFrame === ts) continue;      // already drawn above
        if (c.col != null && (c.col < colMin || c.col > colMax)) continue;
        drawCell(c, p, pos);
        drawn++;
    }
    // The clock crawls (a full day = 10 minutes): 4Hz DOM writes are plenty
    if (ts - lastClockUpdate > 250) {
        lastClockUpdate = ts;
        updateDayClock();
    }
}
